'use client';

import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import Image from "next/image";
import { useRef } from 'react';

import Reveal from "@/components/animation/reveal";
import Cta from "@/components/cta/cta";
import Section from "@/components/section";

export default function AboutAd({ image, link, mobileDashboardImage, desktopDashboardImage, title }) {
  const sectionRef = useRef(null);
  const backgroundImageRef = useRef(null);
  const desktopImageRef = useRef(null);
  const mobileImageRef = useRef(null);

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add({
      isDesktop: '(min-width: 1024px) and (prefers-reduced-motion: no-preference)',
      isMobile: '(max-width: 1023px) and (prefers-reduced-motion: no-preference)',
    }, (context) => {
      const { isDesktop } = context.conditions;

      const scrollTriggerConfig = {
        trigger: sectionRef.current,
        start: 'top bottom',
        end: 'bottom bottom',
        scrub: true,
      };

      gsap.fromTo(desktopImageRef.current, {
        x: isDesktop ? 50 : -20,
      }, {
        x: 0,
        ease: 'none',
        scrollTrigger: scrollTriggerConfig,
      });

      gsap.fromTo(mobileImageRef.current, {
        x: isDesktop ? -50 : 20,
      }, {
        x: 0,
        ease: 'none',
        scrollTrigger: scrollTriggerConfig,
      });

      gsap.fromTo(backgroundImageRef.current, {
        y: 50,
      }, {
        y: -50,
        ease: 'none',
        scrollTrigger: scrollTriggerConfig,
      });
    });
  }, []);

  return (
    <Section theme="main-light" className="pb-6 lg:pb-13.5">
      <div ref={sectionRef} className="relative w-[calc(100%-2rem)] ml-4 rounded-[16px] overflow-hidden lg:w-[calc(100%-5rem)] lg:ml-10 lg:rounded-[14px]">
        <Image unoptimized={true} ref={backgroundImageRef} className="absolute top-[-50px] left-0 right-0 w-full h-[calc(100%+100px)] object-cover will-change-transform" {...image} alt="" aria-hidden="true" loading="lazy" />

        <div className="relative z-1 pt-13.5 pb-23.75 text-main-light lg:pt-17" style={{ '--focus-color': 'var(--color-main-light)' }}>
          <Reveal className="lg:max-w-162.25 lg:mx-auto">
            <h2 className="heading-3-small text-center text-pretty w-[calc(100%-3rem)] mx-6 lg:mx-auto xl:heading-4">{title}</h2>
            <Cta href={link.href} target={link.target} className="w-fit mx-auto mt-8 cta-big">{link.label}</Cta>
          </Reveal>

          <div className="relative flex items-end w-full justify-center mt-13.5 lg:gap-x-9.5 lg:mt-9">
            <Image unoptimized={true} ref={desktopImageRef} className="grow-0 shrink-1 max-lg:w-69 w-[41.3%] h-auto will-change-transform" {...desktopDashboardImage} alt="Ancestry dashboard preview" loading="lazy" />
            <Image unoptimized={true} ref={mobileImageRef} className="grow-0 shrink-1 max-lg:w-21.5 w-[11.6%] max-lg:absolute max-lg:top-11 max-lg:right-4 max-lg:rounded-[10px] max-lg:shadow-[-4px_0_6px_0_rgba(0,0,0,0.2)] will-change-transform" {...mobileDashboardImage} alt="Ancestry mobile app preview" loading="lazy" />
          </div>
        </div>
      </div>
    </Section>
  )
}
